/**
 * Grid bookcase generator (plan §4.1, §4.6): design → BuildModel (the grid's BoardAssembly).
 * Pure, synchronous and fast; never throws for a design-shaped input (invalid values become
 * validation errors). Built on the shared case generator.
 *
 * With one bay, an overlay back and no plinth it reproduces the first slice's `deriveBuild`
 * exactly: same parts, ids, order, text, placements, datums and manufacturing hash.
 */
import type { BuildModel, Design, Issue, OpeningRow, Part, SheetFit } from '../../domain/schemas';
import { SCHEMA_VERSION } from '../../domain/schemas';
import { CONSTRUCTION, limitsOf, type ConstructionProfile } from '../../domain/profiles';
import { hashOf } from '../../domain/hash';
import { fmtMm, fmtNum, sum, toMm } from '../../domain/units';
import type { Datum, Handle, Hardware, Joint, StockUse } from '../assembly';
import { generateCase, type CaseBoard } from '../case';
import { manufacturingHash } from '../hash';
import { faceText, resolveStock, type AnyStock, type ResolvedStock } from '../stock';
import { validateSubstrate } from '../validate';
import { frontsFor } from '../build-common';
import { advisories, parameterErrors, type GridMeasures } from './constraints';
import { bayAvailableTicks, bayCount, bayWidthsOf, FAMILY, layoutOf, stockFor } from './design';
import { baseErrors, baseOf, feetHardware, feetSupports, footKindFor, liftOf, slatErrors, slatsOf } from '../base';

/** Generator version written into every BuildModel, pack and export. */
export const GENERATOR_VERSION = 'cutlist-maker 0.2.0 · families 1.0';
export const GRID_FAMILY_VERSION = '1.0';
export const GRID_LABEL = 'Grid bookcase';

/** Hash of appearance-only settings (name, staging, props, finish, hardware). */
export function appearanceHashOf(design: Pick<Design, 'appearance'>): string {
  return hashOf(design.appearance ?? {});
}

/** Hash of everything that changes what gets cut and built (derives the build). */
export function manufacturingHashOf(design: Design, stock?: AnyStock, construction: ConstructionProfile = CONSTRUCTION): string {
  return deriveBuild(design, stock, construction).manufacturingHash;
}

/** The subset of a build the manufacturing hash covers (see src/families/hash.ts). */
export { manufacturingPayload } from '../hash';

interface FitCheck { fits: boolean; rotated: boolean; note: string; glueUp?: { strips: number; stripWidthTicks: number } }

function sheetFit(l: number, w: number, sheet: { lengthTicks: number; widthTicks: number }, grainAxis: 'length' | 'width', allowRotation: boolean, a: number): FitCheck {
  const sheetL = sheet.lengthTicks - 2 * a;
  const sheetW = sheet.widthTicks - 2 * a;
  const alongL = grainAxis === 'length' ? sheetL : sheetW;
  const acrossL = grainAxis === 'length' ? sheetW : sheetL;
  const desc = `${fmtMm(sheet.lengthTicks)} × ${fmtMm(sheet.widthTicks)}`;
  const straight = l <= alongL && w <= acrossL;
  const rotated = !straight && allowRotation && l <= acrossL && w <= alongL;
  return {
    fits: straight || rotated, rotated,
    note: straight
      ? `Fits one ${desc} sheet with the grain along its length.`
      : rotated
        ? `Fits one ${desc} sheet only with the grain across it.`
        : `Longer than one sheet allows with the grain along its length (max ${fmtMm(sheetL)} × ${fmtMm(sheetW)} mm).`,
  };
}

function boardFit(l: number, w: number, R: ResolvedStock): FitCheck {
  const maxL = R.maxLengthTicks ?? Infinity;
  const maxW = R.maxWidthTicks ?? Infinity;
  if (l > maxL) return { fits: false, rotated: false, note: `Longer than the longest ${R.species.toLowerCase()} board (${fmtMm(maxL)} mm).` };
  if (w > maxW) {
    const strips = Math.ceil(w / maxW);
    const stripWidthTicks = Math.ceil(w / strips);
    return { fits: true, rotated: false, glueUp: { strips, stripWidthTicks }, note: `Glued up from ${strips} strips of ${fmtMm(stripWidthTicks)} mm (boards are sold up to ${fmtMm(maxW)} mm wide).` };
  }
  return { fits: true, rotated: false, note: `From one ${R.species.toLowerCase()} board, ${fmtMm(R.thicknessTicks)} mm thick.` };
}

/**
 * Derive the build model. `stock` and `construction` default to the design's own stock and
 * the grid's joint profile; a passed stock with the design's stock id wins (measured thickness).
 */
export function deriveBuild(design: Design, stock?: AnyStock | null, construction: ConstructionProfile = CONSTRUCTION): BuildModel {
  const stockProfile = stockFor(design, stock);
  const R = resolveStock(stockProfile);
  const { W, H, D } = design;
  const t = R.thicknessTicks;
  const backKind = design.back ?? 'overlay';
  const b = backKind === 'none' ? 0 : R.back.thicknessTicks;
  const foot = footKindFor(R.kind);
  const base = baseOf(design, foot);
  const plinth = base.kind === 'plinth' ? base.height : 0;
  const slats = slatsOf(design);
  const bays = bayCount(design);
  const L = limitsOf(construction);
  const widths = bayWidthsOf(design, stockProfile);
  const availables = Array.from({ length: bays }, (_x, i) => bayAvailableTicks(design, i, stockProfile));
  const measures: GridMeasures = {
    t, b, plinth, bayWidths: widths, availables, limits: L, construction,
    speciesText: R.kind === 'sheet' ? 'ply' : R.species.toLowerCase(), stock: stockProfile,
  };
  const errors: Issue[] = parameterErrors(design, measures);
  // a base other than the first slice's plinth, and slatted sides (FND-08, FND-13)
  if (base.kind !== 'plinth' || base.setback) errors.push(...baseErrors(base, D, foot));
  errors.push(...slatErrors(slats, D));
  if (slats && backKind === 'rebated') errors.push({ field: 'back', message: 'Slatted sides cannot carry a rebated back; use an overlay back or none.', fix: { label: 'Use an overlay back', patch: { back: 'overlay' } } });
  const warnings: Issue[] = advisories(design, measures);
  const dimsFinite = [W, H, D].every(Number.isFinite);

  // --- boards -----------------------------------------------------------------------------
  const a = construction.edgeAllowanceTicks;
  const backFits = (bw: number, bh: number) => {
    const vertical = bh >= bw;
    return sheetFit(vertical ? bh : bw, vertical ? bw : bh, R.back.sheet, R.back.grainAxis, R.back.allowRotation, a).fits;
  };
  // FND-23: a thick solid frame (38 mm boards and up) has mitred corners and through-tenoned shelves
  const thick = R.kind === 'board' && t >= 380;
  const kase = generateCase({
    W, H, D, t, plinth, base, slats, corner: thick ? 'mitre' : 'butt',
    back: { kind: backKind, thickness: b },
    // (through-tenons only in a one-bay case: shelves level either side of a divider would meet in it)
    shelfJoint: design.joinery === 'housed-dado' ? 'housed-dado' : thick && bays === 1 ? 'through-tenon' : 'screwed-butt',
    columns: widths.map((clear, i) => ({ clear, rows: (layoutOf(design, i).openings ?? []).map((g) => ({ clear: g })) })).map((c, i) => {
      // a bay whose openings list is short still lays out n shelves (validation reports it)
      const n = layoutOf(design, i).n;
      const rows = c.rows.slice(0, Math.max(0, n) + 1);
      while (rows.length < Math.max(0, n) + 1) rows.push({ clear: 0 });
      return { ...c, rows };
    }),
    backFits,
  });

  // --- parts ------------------------------------------------------------------------------
  const arris = fmtNum(construction.arrisMm);
  const carcassEdge = R.kind === 'sheet' ? R.edgeText(arris) : R.edgeText(arris);
  const backEdge = backKind === 'rebated' ? `Edges in the rebate; sanded arris ${arris} mm` : `Overlay edges visible; sanded arris ${arris} mm`;
  const cutL = construction.cutAllowanceTicks.l;
  const cutW = construction.cutAllowanceTicks.w;
  const fitByPart = new Map<string, FitCheck>();
  const parts: Part[] = kase.boards.map((q: CaseBoard) => {
    const isBack = q.stock === 'back';
    const fit = isBack
      ? sheetFit(q.l + cutL, q.w + cutW, R.back.sheet, R.back.grainAxis, R.back.allowRotation, a)
      : R.kind === 'sheet'
        ? sheetFit(q.l + cutL, q.w + cutW, R.sheet!, (stockProfile as { grainAxis: 'length' | 'width' }).grainAxis, R.allowRotation, a)
        : boardFit(q.l + cutL, q.w + cutW, R);
    fitByPart.set(q.id, fit);
    const face = isBack ? R.back.faceText : faceText(R, ['side', 'slat', 'post', 'rail', 'divider', 'plinth'].includes(q.role) ? 'out' : 'up');
    const edge = (isBack ? backEdge : carcassEdge) + (q.machining ? `; ${q.machining}` : '');
    const part: Part = {
      id: q.id, role: q.role, label: q.label, qty: 1,
      material: isBack ? R.back.label : R.label,
      stockId: isBack ? `${R.id}:back` : R.id,
      thicknessTicks: q.t,
      finished: { l: q.l, w: q.w, t: q.t },
      cut: { l: q.l + cutL, w: q.w + cutW, t: q.t },
      grainAxis: 'l',
      grainWorld: q.grainWorld,
      face, edge,
      placement: { x: toMm(q.box.x), y: toMm(q.box.y), z: toMm(q.box.z), sx: toMm(q.box.sx), sy: toMm(q.box.sy), sz: toMm(q.box.sz) },
    };
    if (fit.glueUp) part.glueUp = fit.glueUp;
    if (q.datum !== undefined) part.datum = toMm(q.datum);
    if (q.shelfNo !== undefined) part.shelfNo = q.shelfNo;
    if (bays > 1 && q.column !== undefined) part.bay = q.column;
    return part;
  });

  // --- stock fit ----------------------------------------------------------------------------
  const sheetFitList: SheetFit[] = parts.map((p) => {
    const f = fitByPart.get(p.id)!;
    return { partId: p.id, fits: f.fits, rotationUsed: f.rotated, note: f.note };
  });
  if (dimsFinite) {
    for (const f of sheetFitList) {
      if (f.fits) continue;
      const p = parts.find((x) => x.id === f.partId)!;
      if (p.role === 'back') errors.push({ field: p.id, message: p.id === 'back' ? 'Back panel is larger than one sheet.' : `${p.label} is larger than one sheet.` });
      else errors.push({ field: p.id, message: R.kind === 'sheet' ? `${p.label} is longer than one sheet.` : `${p.label} is ${f.note.charAt(0).toLowerCase()}${f.note.slice(1)}` });
    }
  }

  // --- openings, datums, handles -------------------------------------------------------------
  const openingRows: OpeningRow[] = kase.openings.map((o) => {
    const row: OpeningRow = {
      id: o.id, label: o.label,
      bounds: { x0: toMm(o.x0), x1: toMm(o.x1), y0: toMm(o.y0), y1: toMm(o.y1) },
      index: o.row, number: o.row + 1, clear: o.clear, clearMm: toMm(o.clear),
      bottomDatum: toMm(o.y0), topDatum: toMm(o.y1),
    };
    if (bays > 1) row.bay = o.column;
    return row;
  });
  const datums: Datum[] = kase.datums.map((d) => ({ partId: d.partId, label: d.label, mm: toMm(d.ticks), ...(bays > 1 ? { bay: d.column } : {}) }));
  const handles: Handle[] = kase.datums.map((d) => ({ partId: d.partId, parameterKey: 'openings', axis: 'y', index: d.shelfNo, ...(bays > 1 ? { bay: d.column } : {}) }));
  const joints: Joint[] = kase.joints.map((j) => ({ id: `${j.a}~${j.b}`, a: j.a, b: j.b, kind: j.kind, face: j.face, datumMm: j.datum, visible: j.kind === 'housed-dado' || j.kind === 'rebate' || j.kind === 'mortise-tenon' || j.kind === 'through-tenon' || j.kind === 'mitre' }));

  // --- hardware (fixed-scale pieces for the viewer and the pack) ------------------------------------
  const topPart = parts.find((p) => p.id === 'top');
  const hardware: Hardware[] = [];
  const bracket = construction.fasteners.find((f) => f.qtyRule === 'fixed' && /bracket/i.test(f.id));
  if (bracket && bracket.qtyRule === 'fixed' && topPart) {
    hardware.push({
      kind: 'wall-bracket', qty: bracket.qty, spec: bracket.spec, visible: false,
      at: Array.from({ length: bracket.qty }, (_x, i) => ({ partId: 'top', x: toMm(W) * ((i + 1) / (bracket.qty + 1)), y: topPart.placement.y, z: toMm(b) + 10 })),
    });
  }

  if (base.kind === 'feet') hardware.push(...feetHardware(liftOf(base), feetSupports(kase.boards), foot));

  // --- doors and drawers (src/families/fronts.ts): absent for a design without fronts -------------------
  const fr = dimsFinite ? frontsFor(design, parts, openingRows, R, construction) : null;
  if (fr) { parts.push(...fr.parts); sheetFitList.push(...fr.fits); joints.push(...fr.joints); hardware.push(...fr.hardware); errors.push(...fr.errors); warnings.push(...fr.warnings); }
  const Dall = D + (fr?.depthAdd ?? 0);

  // --- warnings never share a field with an error; substrate checks back up the family's ------
  const errorFields = new Set(errors.map((e) => e.field));
  const advisory = warnings.filter((w) => !errorFields.has(w.field));
  if (!errors.length) {
    const sub = validateSubstrate({ parts, joints, envelope: { W: toMm(W), H: toMm(H), D: toMm(Dall) }, stockFit: [], ...(fr ? { hardware } : {}) });
    errors.push(...sub.errors);
  }

  const bay0 = layoutOf(design, 0);
  const A0 = availables[0]!;
  const sheetStock = R.sheet ?? R.back.sheet;
  const stocks: StockUse[] = [
    {
      id: R.id, version: R.version, kind: R.kind, material: R.material, label: R.label, thicknessTicks: R.thicknessTicks,
      ...(R.sheet ? { sheet: R.sheet } : {}), ...(R.maxWidthTicks ? { maxWidthTicks: R.maxWidthTicks } : {}), ...(R.density ? { density: R.density } : {}),
    },
    ...(backKind === 'none' && !parts.some((q) => q.stockId === `${R.id}:back`) ? [] : [{ id: `${R.id}:back`, version: R.version, kind: 'sheet' as const, material: R.kind === 'sheet' ? R.material : 'Plywood', label: R.back.label, thicknessTicks: R.back.thicknessTicks, sheet: R.back.sheet, density: 680 }]),
    ...(fr?.stocks ?? []),
  ];
  const constructionVersion = `${construction.id}@${construction.version}`;
  const build: BuildModel = {
    schemaVersion: SCHEMA_VERSION,
    familyId: FAMILY,
    familyVersion: GRID_FAMILY_VERSION,
    familyLabel: GRID_LABEL,
    family: design.family || FAMILY,
    design: {
      ...design, openings: Array.isArray(design.openings) ? design.openings.slice() : [], appearance: { ...design.appearance },
      ...(design.bayWidths ? { bayWidths: design.bayWidths.slice() } : {}),
      ...(design.bayLayouts ? { bayLayouts: design.bayLayouts.map((l) => ({ ...l, openings: l.openings.slice() })) } : {}),
    },
    stock: stockProfile as BuildModel['stock'],
    construction,
    thickness: { carcass: toMm(t), back: toMm(b) },
    parts,
    joints,
    hardware,
    openings: openingRows,
    handles,
    datums,
    shelfDatums: datums.filter((d) => (d.bay ?? 0) === 0).map((d) => d.mm),
    available: A0, availableMm: toMm(A0),
    openingsSum: sum(bay0.openings ?? []), remainder: A0 - sum(bay0.openings ?? []),
    bays: widths.map((w, i) => {
      const lay = layoutOf(design, i);
      return { index: i, clearWidth: w, n: lay.n, available: availables[i]!, openingsSum: sum(lay.openings ?? []), remainder: availables[i]! - sum(lay.openings ?? []) };
    }),
    envelope: { W: toMm(W), H: toMm(H), D: toMm(Dall), limits: L },
    stocks,
    sheet: { length: toMm(sheetStock.lengthTicks), width: toMm(sheetStock.widthTicks), usableLength: toMm(sheetStock.lengthTicks - 2 * a), usableWidth: toMm(sheetStock.widthTicks - 2 * a) },
    sheetFit: sheetFitList,
    stockFit: sheetFitList,
    panelCount: parts.length,
    validation: { ok: errors.length === 0, errors, warnings: advisory },
    versions: { family: `${FAMILY}@${GRID_FAMILY_VERSION}`, stock: `${R.id}@${R.version}`, joints: constructionVersion, construction: constructionVersion, generator: GENERATOR_VERSION },
    manufacturingHash: manufacturingHash({ parts, openings: openingRows, joints, hardware }),
    appearanceHash: appearanceHashOf(design),
  };
  return build;
}

/** First error message for a field (e.g. 'W', 'openings', 'opening-2'), or null. */
export function errorFor(build: Pick<BuildModel, 'validation'>, field: string): string | null {
  const e = build.validation.errors.find((x) => x.field === field);
  return e ? e.message : null;
}

/** First warning message for a field, or null. */
export function warningFor(build: Pick<BuildModel, 'validation'>, field: string): string | null {
  const w = build.validation.warnings.find((x) => x.field === field);
  return w ? w.message : null;
}
